/** Used in SMS, WhatsApp, and Email template editors */
import { useEffect, useState } from 'react';

import { MessageMedium } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { Delete } from 'ui/icons';

import { CheckboxField, SettingsFieldMoreDetailInRow, TwoRowFieldDisplay } from '.';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { LinkParameterInputProps, LinksCompatibleMediumData } from './types';
import { getIsEditMode } from './utils';
import { FieldExpanded, SettingsReadOnlyText } from './EditorContentContainer.styled';
import { LINK_PARAMETERS_DISPLAY_CONTENT } from './constants';
import {
  AddParamButton,
  DeleteButton,
  KeyValuePairText,
  LinkParameterInputWrapper,
  StyledAddIcon,
  StyledInputText,
} from './styles';
import { isMediumShortLinkCompatible } from './shortLinksHelpers';

const LinkParameterInput = ({
  keyInput,
  valueInput,
  onDelete,
  onKeyChange,
  onValueChange,
}: LinkParameterInputProps) => (
  <LinkParameterInputWrapper>
    <StyledInputText
      id={`link-parameter-key-${keyInput}`}
      onChange={onKeyChange}
      placeholder={LINK_PARAMETERS_DISPLAY_CONTENT.keyPlaceholder}
      type="text"
      value={keyInput}
    />
    <StyledInputText
      id={`link-parameter-value-${valueInput}`}
      onChange={onValueChange}
      placeholder={LINK_PARAMETERS_DISPLAY_CONTENT.valuePlaceholder}
      type="text"
      value={valueInput}
    />
    <DeleteButton onClick={onDelete}>
      <Delete />
      {LINK_PARAMETERS_DISPLAY_CONTENT.removeButton}
    </DeleteButton>
  </LinkParameterInputWrapper>
);

/**
 * True if the checkbox for link params should be checked
 *  Might need to be changed to work for email link parameters:
 *       !!linkParams?.length
 * */

export const LinkParametersFormSection = ({
  isLinkShorteningEnabled,
  isEditDisabled = false,
}: {
  isLinkShorteningEnabled: boolean;
  isEditDisabled?: boolean;
}) => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();

  /** Check if link shortening is enabled */
  const isFormFieldDisabled = isMediumShortLinkCompatible(medium as MessageMedium)
    ? !isLinkShorteningEnabled
    : false;
  const mediumData = (liveTemplate?.mediumData || {}) as LinksCompatibleMediumData;
  const linkParams = mediumData?.linkParams || [];

  /** This is specifically for SMS and WhatsApp, will need to calculate differently for email */
  const linkParamsExist = !!linkParams?.length;
  const [checkboxStatus, setCheckboxStatus] = useState(linkParamsExist);
  const isEditModeVisible = getIsEditMode(view, isEditMode) && !isEditDisabled;

  /**
   * Functions for making changes to link param inputs
   */
  const handleChangeParams = (paramPosition: number, newInputValue: string, isKey: boolean) => {
    const newParams = [...linkParams];
    newParams[paramPosition][isKey ? 'key' : 'value'] = newInputValue.trim();
    setCurrentMediumField('linkParams', newParams);
  };

  const handleDeleteParams = (paramPosition: number) => {
    const newParams = [...linkParams];
    newParams.splice(paramPosition, 1);
    if (newParams.length === 0) {
      setCurrentMediumField('linkParams', null);
    } else {
      setCurrentMediumField('linkParams', newParams);
    }
  };

  const handleAddParam = () =>
    setCurrentMediumField('linkParams', [...linkParams, { key: '', value: '' }]);

  const handleOnCheckbox = () => {
    const newCheckboxStatus = !checkboxStatus;
    setCheckboxStatus(newCheckboxStatus);
    if (!newCheckboxStatus) {
      setCurrentMediumField('linkParams', null);
    }
  };

  /**
   *  Uncheck checkbox if link params is empty upon leaving edit mode.
   *  Check checkbox if link params is not empty on edit mode.
   * */
  useEffect(() => {
    // if read only and no link params
    if (!isEditModeVisible && !linkParams?.length) {
      setCheckboxStatus(false);
      // if there are link params
    } else if (!isLinkShorteningEnabled && isMediumShortLinkCompatible(medium as MessageMedium)) {
      // if link shortening is not enabled, clear link params checkbox
      setCheckboxStatus(false);
    } else if (linkParams?.length) {
      setCheckboxStatus(true);
    }
  }, [isEditModeVisible, linkParams, isLinkShorteningEnabled]);

  return (
    <FormField
      isDisabled={isEditModeVisible && isFormFieldDisabled}
      isHorizontalLayout
      labelText={LINK_PARAMETERS_DISPLAY_CONTENT.labelText}
    >
      {isEditModeVisible ? (
        <>
          <CheckboxField
            isDisabled={isFormFieldDisabled}
            label={LINK_PARAMETERS_DISPLAY_CONTENT.checkboxLabel}
            onClick={() => handleOnCheckbox()}
            status={checkboxStatus}
          />
          {checkboxStatus && (
            <FieldExpanded>
              {linkParams.map(({ key, value }, idx) => (
                <LinkParameterInput
                  key={idx}
                  keyInput={key}
                  valueInput={value}
                  onKeyChange={(newInput) => handleChangeParams(idx, newInput, true)}
                  onValueChange={(newInput) => handleChangeParams(idx, newInput, false)}
                  onDelete={() => handleDeleteParams(idx)}
                />
              ))}
              <AddParamButton onClick={handleAddParam}>
                <StyledAddIcon />
                {LINK_PARAMETERS_DISPLAY_CONTENT.addButton}
              </AddParamButton>
            </FieldExpanded>
          )}
        </>
      ) : (
        <TwoRowFieldDisplay>
          <SettingsReadOnlyText isUnset={!linkParamsExist} data-test="link-param-on-off">
            {linkParamsExist ? 'On' : 'Off'}
          </SettingsReadOnlyText>
          <SettingsFieldMoreDetailInRow data-test="link-param-detail">
            {linkParamsExist &&
              linkParams.map(({ key, value }, idx) => (
                <KeyValuePairText key={idx}>{`${key} = ${value}`}</KeyValuePairText>
              ))}
          </SettingsFieldMoreDetailInRow>
        </TwoRowFieldDisplay>
      )}
    </FormField>
  );
};
